import React from 'react';
import { Settings, Moon, Clock, Calendar } from 'lucide-react';

/**
 * Компонент секции дополнительных опций расчета рабочего времени
 */
const AdditionalOptionsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  return (
    <div className="mb-6 bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg border border-cyan-100 dark:border-cyan-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Settings className="w-5 h-5 mr-2 text-cyan-500" />
        Дополнительные параметры
      </h3>
      
      {/* Ночные смены */}
      <div className="mb-4">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            name="nightShifts"
            checked={formData.nightShifts}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
            <Moon className="w-4 h-4 mr-1 text-cyan-500" />
            Учитывать ночные смены
          </span>
        </label>
        
        {formData.nightShifts && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 ml-6">
            <div>
              <label htmlFor="nightShiftStartHour" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Начало ночной смены (час)
              </label>
              <input
                type="number"
                id="nightShiftStartHour"
                name="nightShiftStartHour"
                value={formData.nightShiftStartHour}
                onChange={handleInputChange}
                min="0"
                max="23"
                className={`form-input w-full ${errors.nightShiftStartHour ? 'input-error' : ''}`}
              />
              {errors.nightShiftStartHour && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.nightShiftStartHour}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                По умолчанию - 22:00
              </p>
            </div>
            
            <div>
              <label htmlFor="nightShiftEndHour" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Окончание ночной смены (час)
              </label>
              <input
                type="number"
                id="nightShiftEndHour"
                name="nightShiftEndHour"
                value={formData.nightShiftEndHour}
                onChange={handleInputChange}
                min="0"
                max="23"
                className={`form-input w-full ${errors.nightShiftEndHour ? 'input-error' : ''}`}
              />
              {errors.nightShiftEndHour && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.nightShiftEndHour}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                По умолчанию - 6:00
              </p>
            </div>
            
            <div>
              <label htmlFor="nightShiftRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Коэффициент доплаты за ночные часы
              </label>
              <input
                type="number"
                id="nightShiftRate"
                name="nightShiftRate"
                value={formData.nightShiftRate}
                onChange={handleInputChange}
                min="1"
                max="5"
                step="0.1"
                className={`form-input w-full ${errors.nightShiftRate ? 'input-error' : ''}`}
              />
              {errors.nightShiftRate && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.nightShiftRate}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                По ТК РФ не менее 1.2
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Сверхурочные часы */}
      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="overtimeHours" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Clock className="w-4 h-4 mr-1 text-cyan-500" />
              Сверхурочные часы
            </label>
            <input
              type="number"
              id="overtimeHours"
              name="overtimeHours"
              value={formData.overtimeHours}
              onChange={handleInputChange}
              min="0"
              max="500"
              className={`form-input w-full ${errors.overtimeHours ? 'input-error' : ''}`}
            />
            {errors.overtimeHours && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.overtimeHours}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="overtimeRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Коэффициент оплаты за сверхурочные
            </label>
            <input
              type="number"
              id="overtimeRate"
              name="overtimeRate"
              value={formData.overtimeRate}
              onChange={handleInputChange}
              min="1"
              max="5"
              step="0.1"
              className={`form-input w-full ${errors.overtimeRate ? 'input-error' : ''}`}
            />
            {errors.overtimeRate && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.overtimeRate}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              По ТК РФ первые 2 часа - 1.5, далее 2.0
            </p>
          </div>
        </div>
      </div>
      
      {/* Работа в выходные */}
      <div className="mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="weekendWork" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Calendar className="w-4 h-4 mr-1 text-cyan-500" />
              Работа в выходные (дней)
            </label>
            <input
              type="number"
              id="weekendWork"
              name="weekendWork"
              value={formData.weekendWork}
              onChange={handleInputChange}
              min="0"
              max="100"
              className={`form-input w-full ${errors.weekendWork ? 'input-error' : ''}`}
            />
            {errors.weekendWork && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.weekendWork}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="weekendRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Коэффициент оплаты за выходные
            </label>
            <input
              type="number"
              id="weekendRate"
              name="weekendRate"
              value={formData.weekendRate}
              onChange={handleInputChange}
              min="1"
              max="5"
              step="0.1"
              className={`form-input w-full ${errors.weekendRate ? 'input-error' : ''}`}
            />
            {errors.weekendRate && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.weekendRate}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              По ТК РФ не менее чем в двойном размере
            </p>
          </div>
        </div>
      </div>
      
      {/* Отпуск и больничные */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="vacationDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Отпуск (дней)
            </label>
            <input
              type="number"
              id="vacationDays"
              name="vacationDays"
              value={formData.vacationDays}
              onChange={handleInputChange}
              min="0"
              max="100"
              className={`form-input w-full ${errors.vacationDays ? 'input-error' : ''}`}
            />
            {errors.vacationDays && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.vacationDays}
              </p>
            )}
          </div>
          
          <div>
            <label htmlFor="sickLeaveDays" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Больничный (дней)
            </label>
            <input
              type="number"
              id="sickLeaveDays"
              name="sickLeaveDays"
              value={formData.sickLeaveDays}
              onChange={handleInputChange}
              min="0"
              max="100"
              className={`form-input w-full ${errors.sickLeaveDays ? 'input-error' : ''}`}
            />
            {errors.sickLeaveDays && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.sickLeaveDays}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* Информация о дополнительных параметрах */}
      <div className="mt-4 bg-white dark:bg-gray-700 p-3 rounded-lg border border-cyan-200 dark:border-cyan-700">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Дополнительные параметры позволяют учесть особенности оплаты труда согласно ТК РФ, 
          включая оплату ночных смен, сверхурочных часов и работы в выходные дни по повышенным тарифам.
        </p>
      </div>
    </div>
  );
};

export default AdditionalOptionsSection;